/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, OnInit, DestroyRef, DoCheck, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CdkDrag, CdkDragHandle, DragRef, Point} from '@angular/cdk/drag-drop';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {WidgetConfigLookup} from '../../../common/models/widget-config.model';
import {LookupService} from '../../../common/service/lookup.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {WidgetService} from '../../services/widget.service';
import {MapModeService} from '../../services/map-mode.service';
import {MapToolsService} from '../../services/map-tools.service';
import {AttachmentsModalService} from '../../../common/component/modal';
import {AppConfigService} from '../../../common/service/app-config.service';
import {TraitsService} from '../../../common/service/traits.service';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {SpatialSequenceService} from '../../../common/service/spatial-sequence.service';
import {AngleUtilService} from '../../services/features/angle/angle-util.service';
import {PolygonUtilService} from '../../services/features/polygon-util.service';
import {PrintAreaService} from '../../../common/service/print-area.service';
import {DataFrameService} from '../../../common/service/data-frame-service';
import {FeaturesApplyInsertService} from '../../services/features-apply-insert.service';
import {AttachmentModel} from '../../../common/models/attachment-model';
import {AngleCreationService} from '../../services/features/angle/angle-creation.service';
import {InterestReconciliationService} from '../../../common/service/interest-reconciliation.service';
import {GeoProcessingService} from '../../services/geo-processing-service';
import {InterestService} from '../../services/features/interest.service';
import {AlertModalService} from '../../../common/component/modal';
import {NgEventBus} from 'ng-event-bus';
import {catchError, concatMap, EMPTY, map, Observable, of, switchMap, tap} from 'rxjs';
import {TenureService} from '../../../plan-information/tenure/tenure.service';
import {ArcGisAuthService} from '../../services/arc-gis-auth.service';
import {HelpLinkComponent} from '../../../help/help-link.component';
import {HELP_LINK_CONTENTS_BASE_URL} from '../../../common/constants';
import {AnalyticsService} from '../../../common/service/analytics.service';
import {MapService} from '../../services/map.service';
import {DisplayPreferencesService} from '../../../common/service/display-preferences.service';

export interface ProjectedCoordSystem {
    id: string;
    name: string;
    description: string;
}

@Component({
    selector: 'nlr-import-survey-data',
    imports: [CommonModule, FormsModule, CdkDrag, CdkDragHandle, HelpLinkComponent, NgSelectModule],
    templateUrl: './import-survey-data.component.html',
    styleUrl: './import-survey-data.component.scss'
})
export class ImportSurveyDataComponent implements OnInit, DoCheck {
    @ViewChild('widget')
    widgetElement: HTMLElement;

    title = 'ImportSurveyDataComponent';
    widgetId = 'importSurveyData';
    widgetConfig: WidgetConfigLookup = {};

    open = true;
    displayProjection = false;

    model = undefined;

    attachmentUUID = null;
    attachmentFilename = '';

    processing = false;
    lastMessage = '';

    projectedCoordSystem: ProjectedCoordSystem = null;
    fileTypes = [];
    allProjectedCoordSystems = [];

    fileTypeSelected = {value: null};
    selectedFileType = '';

    undefinedLegalParcel = undefined;
    legalParcelTypes = [];
    parcelTypes = [];

    createInternalPoints = false;

    designProperties = this.appConfigService.getDesignProperties();
    configFileTypes = this.designProperties.DESIGN_WIDGETS_CONFIGURATION.IMPORT_SURVEY_DATA.FILE_TYPES;

    referenceSearchText = '';

    jobInfo = undefined;

    widgetVisibleAtLastCheck: boolean;

    helpLinkUrl: string = HELP_LINK_CONTENTS_BASE_URL + 'import-help.html';

    helpPageTitle: string = 'Import Survey Data Tool';

    filterProjectedCoordSystemList = undefined;

    constructor(
        private destroyRef: DestroyRef,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private eventBus: NgEventBus,
        private widgetService: WidgetService,
        private mapModeService: MapModeService,
        private mapToolsService: MapToolsService,
        private mapService: MapService,
        private attachmentsModalService: AttachmentsModalService,
        private appConfigService: AppConfigService,
        private traitsService: TraitsService,
        private alertModalService: AlertModalService,
        private spatialUtilService: SpatialUtilService,
        private spatialSequenceService: SpatialSequenceService,
        private angleUtilService: AngleUtilService,
        private polygonUtilService: PolygonUtilService,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private printAreaService: PrintAreaService,
        private dataFrameService: DataFrameService,
        private interestReconciliationService: InterestReconciliationService,
        private interestService: InterestService,
        private angleCreationService: AngleCreationService,
        private geoProcessingService: GeoProcessingService,
        private nlrpTenureService: TenureService,
        private arcGisAuthService: ArcGisAuthService,
        private analyticsService: AnalyticsService,
        private displayPreferencesService: DisplayPreferencesService
    ) {}

    ngOnInit(): void {
        console.debug(this.title + ' : initiated');
        this.widgetConfig = this.lookupService.getLookups('widgetConfig').find((l: WidgetConfigLookup) => l.name === this.widgetId);
        this.model = this.planModelService.getModel().spatialSettings.projectedCoordSystem.description;
        this.setupInitialData();
    }

    ngDoCheck() {
        if (this.widgetElement && !this.widgetVisibleAtLastCheck) {
            this.widgetService.setupWidget(this.widgetId);
        }
        this.widgetVisibleAtLastCheck = !!this.widgetElement;
    }

    computeDragPosition = (userPointerPosition: Point, dragRef: DragRef, dimensions: DOMRect): Point => {
        return this.widgetService.computeDragPosition(userPointerPosition, dragRef, dimensions);
    };

    isWidgetActive() {
        return this.widgetService.isWidgetActive('ImportSurveyData');
    }

    toggleWidgetContentVisibility() {
        this.widgetService.showOrHideWidgetContent('importSurveyData', this.open);
        this.open = !this.open;
    }

    toggleWidgetVisibility() {
        this.widgetService.onClick('ImportSurveyData');
    }

    getSelectedFileType() {
        return this.fileTypeSelected.value !== null ? this.fileTypeSelected.value.id : '';
    }

    updateSelectedItem(item: any) {
        console.debug(this.title + ' : fileTypeChange');
        this.selectedFileType = item.id;
        this.fileTypeSelected.value = item;

        this.filterProjectedCoordSystem();
        this.clear();
        this.checkSettings();
    }

    updateProjectedCoordSystem(selectedCoordSystem: any) {
        console.debug(this.title + ' : projectedCoordSystemChange');
        this.projectedCoordSystem = structuredClone(selectedCoordSystem);
    }

    importBtnDisabled() {
        return (
            this.mapModeService.mapMode.type !== 'mainPlan' ||
            this.mapToolsService.isActive('DISPLAY_MODE_PRINT_PREVIEW') ||
            !this.attachmentFilename ||
            this.processing ||
            (this.fileTypeSelected && this.fileTypeSelected.value && this.fileTypeSelected.value.projectionRequired && !this.projectedCoordSystem)
        );
    }

    getLotId() {
        console.debug(this.title + ' : getLotId');
        if (this.planModelService.getModel().formerTenures !== undefined) {
            if (this.planModelService.getModel().formerTenures[0].lots.length > 0) {
                return this.planModelService.getModel().formerTenures[0].lots[0].lotId;
            } else {
                console.error(this.title + ' :  No lots found in former tenure : ' + this.planModelService.getModel().formerTenures[0]);
            }
        } else {
            console.error(this.title + ' :  formerTenures expected in model.');
        }
    }

    runGP(parentLotX: any, parentLotY: any) {
        const gpUrl = (this.configFileTypes.find(v => v.FILE_TYPE === this.fileTypeSelected.value.id) || this.configFileTypes[0]).GP_URL;

        this.processing = true;
        this.lastMessage = 'Uploading...';

        const geoProcessingParameters = {
            ParentLotX: parentLotX,
            ParentLotY: parentLotY,
            FileID: this.attachmentUUID,
            FileName: this.attachmentFilename,
            ReferenceScale: 250,
            PlanSpatialRefName: this.planModelService.getModel().spatialSettings.projectedCoordSystem.name,
            FileSpatialRefName: this.projectedCoordSystem?.name,
            Debug: 'false',
            DefaultParcelType: this.planModelService.getModel().planPurpose.defaultParcelType,
            createInterPoints: this.createInternalPoints.toString()
        };

        return this.geoProcessingService.callGeoProcessingService(gpUrl + this.arcGisAuthService.getTokenParam(), geoProcessingParameters, false);
    }

    submit() {
        console.debug(`${this.title} : submit`);

        if (
            this.fileTypeSelected.value.id !== 'CSD' &&
            this.projectedCoordSystem.name !== 'Planar' &&
            this.projectedCoordSystem.name !== this.planModelService.getModel().spatialSettings.projectedCoordSystem.name
        ) {
            this.alertModalService.createAlertMessage(
                "The file cannot be imported! The projected coordinate system selected in the 'Import Survey Data' function does not match the" +
                    ' projected coordinate system specified in the Plan System.'
            );

            return;
        }

        const hasNLOP = this.planModelService.getModel().formerTenures.some(formerTenure => {
            return formerTenure && !formerTenure.parentPlanType;
        });

        const hasLots = this.planModelService.getModel().formerTenures.some(formerTenure => {
            return (
                formerTenure &&
                formerTenure.lots &&
                formerTenure.lots.length &&
                formerTenure.lots.some(function (lot) {
                    return lot.lotId;
                })
            );
        });

        const subscription = this.mapToolsService
            .activate('IMPORT', () => {})
            .pipe(
                concatMap(() => {
                    if (hasNLOP || !hasLots) {
                        console.debug(this.title + hasNLOP ? ' : Has NLOP' : ' : Has No Lots Available or No Lots with lotIds');
                        const parentCentroidXY = hasNLOP ? -2 : -1;
                        return this.runGP(parentCentroidXY, parentCentroidXY);
                    }

                    const uniqueLotIds = this.planModelService.getModel().formerTenures.reduce(function (accumulator, currentValue) {
                        currentValue.lots.forEach(function (lot) {
                            if (accumulator.indexOf(lot.lotId) === -1) {
                                accumulator.push(lot.lotId);
                            }
                        });
                        return accumulator;
                    }, []);

                    return this.nlrpTenureService.getCentroid(uniqueLotIds).pipe(
                        concatMap(centroid => {
                            if (centroid['x'] === 0 && centroid['y'] === 0) {
                                console.debug(this.title + ' : submit - error retrieving centroid');
                                this.alertModalService.createAlertMessage(
                                    'Unable to render the plan as the former tenure has no spatial component in the database.'
                                );
                                return EMPTY;
                            }

                            if (this.fileTypeSelected.value.id === 'DWG' && this.projectedCoordSystem?.name !== 'Planar') {
                                centroid['x'] = -1;
                                centroid['y'] = -1;
                            }

                            return this.runGP(centroid['x'], centroid['y']);
                        })
                    );
                })
            )
            .subscribe({
                next: jobInfo => {
                    this.jobInfo = jobInfo;
                    this.gpJobComplete(jobInfo);
                },
                error: jobInfo => {
                    this.gpJobFailed(jobInfo);
                },
                complete: () => {
                    this.gpJobStatus();
                }
            });

        this.eventBus.cast('nlr:busy', subscription);
    }

    applyDWGPolygonCorrection(polygonFeatures: any, lineFeatures: any, pointFeatures: any) {
        if (this.fileTypeSelected.value.id === 'DWG') {
            const polygonsWithLineIds = polygonFeatures.filter(feature => {
                const lines = [];
                const lineIds = feature.attributes.lineids;
                lineIds.split(',').map(id => {
                    console.log(id);
                    lineFeatures.map(line => {
                        if (line.attributes.id === parseInt(id.replace(/\[/g, '').replace(/\]/g, ''))) {
                            lines.push(line);
                        }
                    });
                });

                if (lines.length > 0) {
                    const polygonRingInfo = this.polygonUtilService.getPolygonRing(lines, pointFeatures);
                    feature.attributes.lineids = polygonRingInfo.lineids;
                }

                return feature.attributes.lineids;
            });

            if (polygonsWithLineIds.length < polygonFeatures.length) {
                polygonFeatures.splice(0, polygonFeatures.length, ...polygonsWithLineIds);
            }
        }
    }

    getFeaturesCount(featureResult: any) {
        return featureResult.value.features !== undefined ? featureResult.value.features.length : 0;
    }

    gpJobComplete(jobInfo: any) {
        console.debug(this.title + ' : gpJobComplete');

        this.processing = false;

        if (jobInfo.jobStatus === 'esriJobSucceeded') {
            this.lastMessage = 'File processed successfully.';

            const sequenceFeaturesDataSet = {angle: {}, point: {}, line: {}, polygon: {}, lot: {}};

            console.log(sequenceFeaturesDataSet);

            this.analyticsService.event('import_survey_data', {
                event_category: 'spatial',
                event_label: 'import_type',
                value: this.fileTypeSelected?.value?.id
            });

            const anglesCount = this.getFeaturesCount(jobInfo.results.Angle.featureResult);
            const pointsCount = this.getFeaturesCount(jobInfo.results.Point.featureResult);
            const linesCount = this.getFeaturesCount(jobInfo.results.Line.featureResult);
            const polygonsCount = this.getFeaturesCount(jobInfo.results.Polygon.featureResult);
            const lotsCount = this.getFeaturesCount(jobInfo.results.Lot.featureResult);

            if (pointsCount === 0) {
                this.alertModalService.createAlertMessage('An error occurred while processing the file. No points were created.', 'Ok');
            }

            if (this.fileTypeSelected.value.id === 'DWG') {
                const points = jobInfo.results.Point.featureResult.value.features;
                if (points.length) {
                    const spatialReference = new (window as any).esri.SpatialReference(this.planModelService.getSpatialReference());
                    const pointGeometries = points.map(p => p.geometry);

                    if (
                        spatialReference.isGeographic
                            ? pointGeometries.some(g => g.x < -180 || g.x > 180 || g.y < -90 || g.y > 90)
                            : this.spatialUtilService.project(pointGeometries, {wkid: 4283}).some(g => !g)
                    ) {
                        this.alertModalService.createAlertMessage('The coordinate system of the DWG must match the selected coordinate system.');
                        return;
                    }

                    points.map(feature => {
                        feature.geometry.x = feature.attributes.x;
                        feature.geometry.y = feature.attributes.y;
                    });
                }
            }

            const planPurpose = this.planModelService.getModel().planPurpose;

            if (this.fileTypeSelected.value.id === 'DWG' || this.fileTypeSelected.value.id === 'CSD') {
                jobInfo.results.Polygon.featureResult.value.features.map(feature => {
                    if (
                        !this.legalParcelTypes
                            .filter(l => {
                                return this.traitsService.criteriaMatches(l.planTraitCriteria, false);
                            })
                            .some(parcelType => {
                                return parcelType.id === feature.attributes.type;
                            })
                    ) {
                        feature.attributes.type = this.undefinedLegalParcel.id;
                    }
                });

                if (jobInfo.results.Lot.featureResult.value.features) {
                    jobInfo.results.Lot.featureResult.value.features.filter(feature => {
                        this.parcelTypes
                            .filter(l => {
                                this.traitsService.criteriaMatches(l.planTraitCriteria, false);
                            })
                            .some(parcelType => parcelType.id === feature.attributes.type);
                    });
                }
            }

            let anyUndefined = false;

            jobInfo.results.Polygon.featureResult.value.features.map(feature => {
                if (feature.attributes.type === this.undefinedLegalParcel.id) {
                    feature.attributes.lotid = undefined;
                    feature.attributes.roadid = undefined;
                    feature.attributes.interestids = undefined;
                    anyUndefined = true;
                }
            });

            if (this.fileTypeSelected.value.id === 'CSD') {
                const coordMode = jobInfo.results.CoordMode.featureResult.value;

                if (
                    !this.allProjectedCoordSystems
                        .filter(v => this.traitsService.criteriaMatches(v.planTraitCriteria, true))
                        .some(v => v.coordMode === coordMode)
                ) {
                    const errorItem = this.allProjectedCoordSystems.filter(function (i) {
                        return i.coordMode === coordMode;
                    });

                    this.alertModalService.createAlertMessage(
                        errorItem[0].errorMessage1
                            .replace('[tenurename]', this.planModelService.getModel().tenureType.name)
                            .replace('[planpurpose]', planPurpose.name)
                    );
                    return;
                }
            }

            if (this.fileTypeSelected.value.id === 'CSD' || this.fileTypeSelected.value.id === 'DWG') {
                if (anyUndefined) {
                    this.alertModalService.createAlertMessage(
                        `The ${this.fileTypeSelected.value.id} contains polygon types not permitted for ` +
                            ` ${this.planModelService.getModel().tenureType.name} ${planPurpose.name} plan. These polygons have been created as` +
                            ` undefined polygons in NLR. Please review these polygons in the attribute viewer.`,
                        null,
                        'Warning message',
                        'warning'
                    );
                }
            }

            const pointSequence = this.spatialSequenceService.getPointSequence(pointsCount);
            const lineSequence = this.spatialSequenceService.getLineSequence(linesCount);
            const polygonSequence = this.spatialSequenceService.getPolygonSequence(polygonsCount);
            const angleSequence = this.spatialSequenceService.getAngleSequence(anglesCount);
            const lotSequence = this.spatialSequenceService.getLotSequence(lotsCount);

            this.setSequenceIds(pointSequence, jobInfo.results.Point.featureResult.value.features, sequenceFeaturesDataSet.point);
            this.setSequenceIds(lineSequence, jobInfo.results.Line.featureResult.value.features, sequenceFeaturesDataSet.line);
            this.setSequenceIds(polygonSequence, jobInfo.results.Polygon.featureResult.value.features, sequenceFeaturesDataSet.polygon);
            this.setSequenceIds(angleSequence, jobInfo.results.Angle.featureResult.value.features, sequenceFeaturesDataSet.angle);
            this.setSequenceIds(lotSequence, jobInfo.results.Lot.featureResult.value.features, sequenceFeaturesDataSet.lot);

            this.clear();

            this.assignSequenceIdsFromLegacyIdsToAngles(jobInfo.results, sequenceFeaturesDataSet);
            this.assignSequenceIdsFromLegacyIdsToLines(jobInfo.results, sequenceFeaturesDataSet);
            this.assignSequenceIdsFromLegacyIdsToPolygons(jobInfo.results, sequenceFeaturesDataSet);
            this.assignSequenceIdsFromLegacyIdsToLots(jobInfo.results, sequenceFeaturesDataSet);

            this.convertLinesBearingToDMS(jobInfo.results.Line.featureResult.value.features);
            this.applyDWGPolygonCorrection(
                jobInfo.results.Polygon.featureResult.value.features,
                jobInfo.results.Line.featureResult.value.features,
                jobInfo.results.Point.featureResult.value.features
            );

            this.spatialUtilService.setLotAcquisitionTransferActionDefaults(jobInfo.results.Lot.featureResult.value.features);

            const observable: Observable<any> =
                this.spatialUtilService.getPrintAreas().length === 0 && this.spatialUtilService.getPoints().length === 0
                    ? this.createPrintArea(jobInfo)
                    : of(null);

            observable
                .pipe(
                    switchMap(() => {
                        jobInfo.results.Angle.featureResult.value.features = this.angleUtilService.completeAnglesCreation(
                            jobInfo.results.Angle.featureResult.value.features,
                            jobInfo.results.Point.featureResult.value.features,
                            jobInfo.results.Line.featureResult.value.features,
                            true,
                            undefined
                        );

                        const generatedCadastralAngles =
                            this.fileTypeSelected.value.id !== 'DWG'
                                ? this.angleCreationService.createMissingCadastralAngles(
                                      jobInfo.results.Angle.featureResult.value.features,
                                      jobInfo.results.Point.featureResult.value.features,
                                      jobInfo.results.Line.featureResult.value.features,
                                      this.angleCreationService.ANGLE_GENERATION_METHOD.IMPORT,
                                      false
                                  )
                                : [];

                        const sequence = this.spatialSequenceService.getAngleSequence(generatedCadastralAngles.length);

                        generatedCadastralAngles.map((angle, featureIndex) => {
                            angle.attributes.id = sequence[featureIndex];
                        });

                        Array.prototype.push.apply(jobInfo.results.Angle.featureResult.value.features, generatedCadastralAngles);

                        return this.featuresApplyInsertService.insertFeatures(jobInfo.results).pipe(
                            map(data => {
                                this.eventBus.cast(this.designProperties.EVENTS.FEATURES_CREATED, {
                                    features: jobInfo.results.Angle.featureResult.value.features,
                                    featureLayer: this.spatialUtilService.getAngleMapLayer()
                                });

                                this.widgetService.onClick('ImportSurveyData');
                                const nonSpatialInterests = this.interestReconciliationService.getNonSpatialInterests(
                                    this.planModelService.getModel().formerTenures,
                                    this.planModelService.getModel().interests
                                );

                                this.interestService.updateInterestsWithNoGeometry(nonSpatialInterests);

                                this.printAreaService.setReferenceScaleLookup(null);

                                return data;
                            })
                        );
                    }),
                    catchError(err => {
                        console.error(`${this.title} - gpJobComplete - error inserting features:`, err);
                        this.alertModalService.createAlertMessage('An error occurred while inserting the features.');
                        return of(null);
                    }),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(result => {
                    console.log(result);
                });
        } else if (jobInfo.jobStatus === 'esriJobFailed') {
            this.alertModalService.createAlertMessage('An error occurred while processing the file.');

            jobInfo['messages'].map(message => {
                if (message['type'] === 'esriJobMessageTypeError') {
                    const msg = message['description'];
                    const errorTypes = [
                        'NoFormerTenure',
                        'NLOPNotAllowed',
                        'GCSNotAllowed',
                        'SpatialRefMismatch',
                        'UnsupportedCSDType',
                        'MissingGeometryType',
                        'InvalidCoordInfo',
                        'InvalidAngle'
                    ];

                    errorTypes.forEach(error => {
                        if (msg.includes(error) && !msg.includes('Traceback')) {
                            const errorType = JSON.parse(msg);
                            this.alertModalService.createAlertMessage(errorType[error]);
                        }
                    });

                    console.debug(message['type'] + ' : ' + message['description']);
                }
            });
        }
    }

    createPrintArea(jobInfo: any) {
        console.log(`createPrintArea is triggered!`);

        const points = jobInfo.results.Point.featureResult.value.features;
        const pointsExtent = new (window as any).esri.geometry.Extent(
            [...points]
                .map(f => f.geometry)
                .reduce((acc, g) => {
                    if (!acc.spatialReference) {
                        acc.spatialReference = g.spatialReference.toJson();
                    }

                    acc.xmin = typeof acc.xmin === 'number' && !isNaN(acc.xmin) ? Math.min(acc.xmin, g.x) : g.x;
                    acc.xmax = typeof acc.xmax === 'number' && !isNaN(acc.xmax) ? Math.max(acc.xmax, g.x) : g.x;
                    acc.ymin = typeof acc.ymin === 'number' && !isNaN(acc.ymin) ? Math.min(acc.ymin, g.y) : g.y;
                    acc.ymax = typeof acc.ymax === 'number' && !isNaN(acc.ymax) ? Math.max(acc.ymax, g.y) : g.y;
                    return acc;
                }, {})
        );
        console.log(pointsExtent);

        const coordSysId = this.planModelService.getModel().spatialSettings.projectedCoordSystem.id;
        const paperSizeId = Number(this.printAreaService.getPaperSizeLookupId());
        const qtdPerPage = 1;

        const referenceScaleLookup =
            this.printAreaService.getReferenceScaleLookups().find(l => {
                const df = this.dataFrameService.getDataFrame(coordSysId, paperSizeId, l.divisor, qtdPerPage, pointsExtent);
                const dfExtent = new (window as any).esri.geometry.Extent(df.xmin, df.ymin, df.xmax, df.ymax, pointsExtent.spatialReference);
                return dfExtent.contains(new (window as any).esri.geometry.Extent(pointsExtent).expand(1.1));
            }) || this.printAreaService.getReferenceScaleLookup();

        if (referenceScaleLookup) {
            this.printAreaService.setReferenceScaleLookup(referenceScaleLookup);
        }

        const dataFrameExtent = this.dataFrameService.getDataFrame(coordSysId, paperSizeId, referenceScaleLookup.divisor, qtdPerPage, pointsExtent);

        const printAreaFeature = {
            geometry: new (window as any).esri.geometry.Polygon({
                spatialReference: this.planModelService.getSpatialReference(),
                rings: [
                    [
                        [dataFrameExtent.xmin, dataFrameExtent.ymin],
                        [dataFrameExtent.xmin, dataFrameExtent.ymax],
                        [dataFrameExtent.xmax, dataFrameExtent.ymax],
                        [dataFrameExtent.xmax, dataFrameExtent.ymin],
                        [dataFrameExtent.xmin, dataFrameExtent.ymin]
                    ]
                ]
            }),
            attributes: {
                guid: this.planModelService.getGuid(),
                id: 1,
                type: 1,
                papersize: paperSizeId,
                scale: referenceScaleLookup.id
            }
        };

        const scaleRatio = referenceScaleLookup.divisor / this.printAreaService.getReferenceScaleDivisor();

        return this.spatialUtilService.applyEdits('printArea', [printAreaFeature], null, null).pipe(
            tap(() => {
                if (this.mapModeService.mapMode.type === 'mainPlan') {
                    this.mapService.resetMapServiceLayer(referenceScaleLookup.divisor, true, scaleRatio);
                    this.mapService.setMapMode(false, this.displayPreferencesService.displayPreferences);
                }
            })
        );
    }

    convertLinesBearingToDMS(lines: any) {
        console.debug(this.title + ' : convertLinesBearingToDMS');

        lines.map(feature => {
            feature.attributes.bearing = this.angleUtilService.ddToDms(feature.attributes.bearing);
        });
    }

    setSequenceIds(data: any, features: any, dataSet: any) {
        console.debug(this.title + ' : setSequenceIds');

        features.map((feature, featureIndex) => {
            dataSet[feature.attributes.id] = data[featureIndex];
            feature.attributes.id = data[featureIndex];
        });
    }

    assignSequenceIdsFromLegacyIdsToAngles(results: any, sequenceFeaturesDataSet: any) {
        console.debug(this.title + ' : assignSequenceIdsFromLegacyIdsToAngles');

        results.Angle.featureResult.value.features.map(feature => {
            feature.attributes.pointid = sequenceFeaturesDataSet.point[feature.attributes.pointid];
            feature.attributes.anglefromlineid = sequenceFeaturesDataSet.line[feature.attributes.anglefromlineid];
            feature.attributes.angletolineid = sequenceFeaturesDataSet.line[feature.attributes.angletolineid];
        });
    }

    assignSequenceIdsFromLegacyIdsToLines(results: any, sequenceFeaturesDataSet: any) {
        console.debug(this.title + ' : assignSequenceIdsFromLegacyIdsToLines');

        results.Line.featureResult.value.features.map(feature => {
            feature.attributes.frompointid = sequenceFeaturesDataSet.point[feature.attributes.frompointid];
            feature.attributes.topointid = sequenceFeaturesDataSet.point[feature.attributes.topointid];
        });
    }

    assignSequenceIdsFromLegacyIdsToPolygons(results: any, sequenceFeaturesDataSet: any) {
        console.debug(this.title + ' : assignSequenceIdsFromLegacyIdsToPolygons');

        results.Polygon.featureResult.value.features.map(polygon => {
            if (polygon.attributes.lotid !== null) {
                polygon.attributes.lotid = sequenceFeaturesDataSet.lot[polygon.attributes.lotid];
            }

            const lineIds = polygon.attributes.lineids.split(',');

            lineIds.map((legacyLineId, legacyLineIdIndex) => {
                lineIds[legacyLineIdIndex] = '[' + sequenceFeaturesDataSet.line[legacyLineId.replace(/\[/g, '').replace(/\]/g, '')] + ']';
            });

            polygon.attributes.lineids = lineIds.join();
        });
    }

    assignSequenceIdsFromLegacyIdsToLots(results: any, sequenceFeaturesDataSet: any) {
        console.debug(this.title + ' : assignSequenceIdsFromLegacyIdsToLots');

        results.Lot.featureResult.value.features.map(lot => {
            const polygonIds = lot.attributes.polygonids.split(',');

            polygonIds.map((legacyPolygonId, legacyPolygonIdIndex) => {
                polygonIds[legacyPolygonIdIndex] = '[' + sequenceFeaturesDataSet.polygon[legacyPolygonId.replace(/\[/g, '').replace(/\]/g, '')] + ']';
            });

            lot.attributes.polygonids = polygonIds.length ? polygonIds.join() : null;
        });
    }

    gpJobStatus() {
        console.debug(this.title + ' : gpJobStatus. Status ' + this.jobInfo);

        if (this.jobInfo['messages'].length > 0) {
            this.lastMessage = this.jobInfo.messages[this.jobInfo['messages'].length - 1].description;
        }
    }

    gpJobFailed(jobInfo: any) {
        this.lastMessage = 'Failed.';
        this.processing = false;

        const messageArray = [];
        messageArray.push('An error occurred while processing the file\n');

        jobInfo.messages.forEach(message => {
            const msg = message.description;
            const errorTypes = [
                'NoFormerTenure',
                'NLOPNotAllowed',
                'GCSNotAllowed',
                'SpatialRefMismatch',
                'UnsupportedCSDType',
                'MissingGeometryType',
                'InvalidCoordInfo',
                'InvalidAngle'
            ];

            errorTypes.forEach(error => {
                if (msg.includes(error) && !msg.includes('Traceback')) {
                    const errorType = JSON.parse(msg);
                    messageArray.push(errorType[error]);
                }
                console.debug(message.type + ' : ' + message.description);
            });
        });

        this.alertModalService.createAlertMessage(messageArray);
    }

    attachFileClick() {
        console.debug(`${this.title} : attachFileClick`);

        const fileExtensions = this.fileTypeSelected.value.fileExtension.split(' ');
        const uploadsPrefix = this.designProperties.UPLOADS_PREFIX;

        this.attachmentsModalService.showModal(fileExtensions, uploadsPrefix).subscribe((data: AttachmentModel) => {
            this.attachmentUUID = data.uuid.split('/').pop() ?? '';
            this.attachmentFilename = data.fileName;
        });
    }

    setupInitialData() {
        console.debug(this.title + ' : setupInitialData');
        this.allProjectedCoordSystems = this.lookupService.getLookups('projectedCoordSystem');
        this.fileTypes = this.lookupService.getLookups('surveyDataFileType');
        this.fileTypeSelected.value = this.fileTypes[0];
        this.selectedFileType = this.fileTypeSelected.value.id;
        this.checkSettings();

        this.legalParcelTypes = this.lookupService.getLookups('legalParcelType');

        this.legalParcelTypes.forEach(l => {
            if (l.businessClass === 'Undefined') {
                this.undefinedLegalParcel = structuredClone(l);
            }
        });

        this.parcelTypes = this.lookupService.getLookups('parcelType');

        this.createInternalPoints = false;
    }

    clear() {
        console.debug(this.title + ' : clear');
        this.projectedCoordSystem = null;
        this.attachmentFilename = '';
    }

    checkSettings() {
        console.debug(this.title + ' : checkSettings');
        this.displayProjection = this.fileTypeSelected.value.projectionRequired === true;
    }

    shouldAnglesBeCreated() {
        console.debug(this.title + ' : shouldAnglesBeCreated');
        return this.planModelService.getModel().surveyMethod.angleCreationPermitted === true;
    }

    createInternalPointsChanged() {
        this.createInternalPoints = !this.createInternalPoints;
    }

    filterProjectedCoordSystem() {
        const coordinateSystemLookup = this.allProjectedCoordSystems.filter(projectedCoordSystem =>
            projectedCoordSystem.description.includes(this.referenceSearchText)
        );

        const selected = this.selectedFileType ? this.selectedFileType : undefined;

        if (selected === 'DWG' || selected === 'CSD') {
            this.filterProjectedCoordSystemList = coordinateSystemLookup
                .filter(coordinateSystem => {
                    return this.filterProjectedCoordSystemForCsdOrDwg(coordinateSystem);
                })
                .sort((a, b) => a.uiOptionOrder - b.uiOptionOrder);
        } else if (selected === 'ASCII') {
            this.filterProjectedCoordSystemList = coordinateSystemLookup
                .filter(coordinateSystem => {
                    return this.filterProjectedCoordSystemForAscii(coordinateSystem);
                })
                .sort((a, b) => a.uiOptionOrder - b.uiOptionOrder);
        } else {
            console.error(`Unsupported survey data import method ${selected}`);
            this.filterProjectedCoordSystemList = [];
        }
    }

    projectedCoordSystemTrackBy(value: ProjectedCoordSystem) {
        return value.id;
    }

    filterProjectedCoordSystemForCsdOrDwg(coordinateSystemLookup: any) {
        return this.traitsService.criteriaMatches(coordinateSystemLookup.planTraitCriteria, true);
    }

    filterProjectedCoordSystemForAscii(coordinateSystemLookup: any) {
        return coordinateSystemLookup.code !== 'PLANE';
    }

    private resetObject(obj: ProjectedCoordSystem): void {
        Object.keys(obj).forEach(key => {
            if (obj[key] !== null) obj[key as keyof ProjectedCoordSystem] = '';
        });
    }
}
